import { getAppConfig } from "@src/config/legacyKitConfig";
import { secureStorage, SecureStorageKeys } from "@src/services/secure-storage";
import { AssetCameraKey, AssetId } from "@visioni/a3s-schema";

export interface JpegStreamResponse {
  image: {
    url: string;
  } | null;
}

export class LegacyStreamError extends Error {
  constructor(
    public status: number,
    public statusText: string,
    message?: string,
  ) {
    super(message || `Legacy Stream Error: ${status} ${statusText}`);
    this.name = "LegacyStreamError";
  }
}

export async function getJpegStreamUrl(cameraId: string): Promise<string | null> {
  const config = getAppConfig();
  const baseUrl = config.apiBaseUrl;
  const url = `${baseUrl}/api/cameras/${cameraId}/stream/`;

  const token = await secureStorage.get(SecureStorageKeys.legacyAuthToken);
  if (!token) {
    throw new LegacyStreamError(401, "Unauthorized", "No legacy auth token available");
  }

  const response = await fetch(url, {
    method: "GET",
    headers: {
      Authorization: `Token ${token}`,
      Accept: "application/json",
    },
  });

  if (!response.ok) {
    throw new LegacyStreamError(response.status, response.statusText);
  }

  const data: JpegStreamResponse = await response.json();
  return data.image?.url ?? null;
}

export function extractCameraIdForLegacyApi(cameraKey: string): string {
  try {
    const key = AssetCameraKey.fromString(cameraKey);
    const parts = AssetId.toParts(AssetCameraKey.asset(key));

    if (parts.fleet === "G1") {
      const base64 = parts.unique.replace(/-/g, "+").replace(/_/g, "/");
      const decoded = atob(base64);
      const hex = Array.from(decoded, (c) => c.charCodeAt(0).toString(16).padStart(2, "0")).join("");
      return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
    }

    return cameraKey.split("@")[1] || cameraKey;
  } catch {
    const parts = cameraKey.split("@");
    return parts[1] || parts[0];
  }
}
